import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export default function Selector({ options, value, onChange, className, getOptionLabel = (option) => option.title }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const selectorRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  // Находим объект текущего выбранного значения
  const selectedOption = options.find((option) => option.value === value);

  // Обновление позиции выпадающего списка
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    const listHeight = Math.min(options.length * 36, 180);

    const opensUp = window.innerHeight - rect.bottom < listHeight + 8 && rect.top > listHeight + 8;

    setPosition({
      top: opensUp ? rect.top - listHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [options.length]);

  // Выбор элемента
  const handleSelect = (option) => {
    setIsOpen(false);

    // Передаём наверх только value
    onChange?.(option.value);
  };

  // Обновляем позицию при открытии
  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  // Закрытие при клике вне селектора и вне выпадающего списка
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const target = event.target;

      const clickedInsideSelector = selectorRef.current?.contains(target);

      const clickedInsideList = listRef.current?.contains(target);

      if (clickedInsideSelector || clickedInsideList) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`${className}-selector`} ref={selectorRef}>
      <button
        ref={buttonRef}
        className={`${className}-selector__button  flex-row`}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={`${className}-selector__text`}>{selectedOption ? getOptionLabel(selectedOption) : value}</span>

        <span className={`${className}-selector__arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            className={`${className}-selector__list`}
            role="listbox"
            aria-label={`${className} options`}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: 999999,
            }}
          >
            {options.map((option) => {
              const isSelected = value === option.value;

              return (
                <button
                  key={`${className}-${option.title}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`${className}-selector__item button ${isSelected ? `${className}-selector__item--selected` : ""}`}
                  onClick={() => handleSelect(option)}
                  disabled={isSelected}
                >
                  {getOptionLabel(option)}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
